import { ApiError, api } from '../onboarding/api.ts';
import { discoveryClient } from '../discovery/client.ts';
import { findingTaskLane, isSecurityFinding } from '../discovery/evidenceSummary.ts';
import type { Evidence, Finding, SourceRecord, WorkspaceCredentials } from './types.ts';

function canonicalFindingId(finding: { findingId: string }): string {
  return finding.findingId.trim();
}

export function isPersistedFinding(row: unknown): row is Finding {
  return Boolean(
    row &&
    typeof row === 'object' &&
    !Array.isArray(row) &&
    typeof (row as Finding).findingId === 'string' &&
    (row as Finding).findingId.trim().length > 0 &&
    typeof (row as Finding).title === 'string' &&
    (row as Finding).title.length > 0 &&
    (row as Finding).title.trim().length > 0,
  );
}

type FindingsListFilters = {
  severity?: string;
  lane?: 'DNS' | 'Network' | 'Mixed';
  includeRetainedEvidence?: boolean;
};

async function retainedEvidence(workspaceId: string, credentials: WorkspaceCredentials) {
  const evidence = new Map<string, Evidence>();
  const sources = new Map<string, SourceRecord>();
  const cursors = new Set<string>();
  let after: string | undefined;
  do {
    const page = await api.getDiscoveryEvidence(
      workspaceId,
      credentials.session,
      credentials.token,
      after,
    );
    for (const item of page.evidence) evidence.set(item.evidenceId, item);
    for (const item of page.sourceRecords) sources.set(item.sourceRecordId, item);
    after = page.nextCursor ?? undefined;
    if (after && cursors.has(after)) throw new Error('Evidence pagination did not advance');
    if (after) cursors.add(after);
  } while (after);
  return { evidence: [...evidence.values()], sourceRecords: [...sources.values()] };
}

async function latestAttempt(
  workspaceId: string,
  credentials: WorkspaceCredentials = {},
  filters: FindingsListFilters = {},
) {
  const [records, persisted] = await Promise.all([
    discoveryClient.latestAttempt(workspaceId, credentials),
    discoveryClient.findings(workspaceId, credentials, filters).catch(() => []),
  ]);
  const history = filters.includeRetainedEvidence
    ? await retainedEvidence(workspaceId, credentials)
    : null;
  const snapshotFindings = (Array.isArray(records?.findings) ? records.findings : []).filter(
    isPersistedFinding,
  );
  const persistedFindings = (Array.isArray(persisted) ? persisted : []).filter(isPersistedFinding);
  const byId = new Map<string, Finding>();
  for (const finding of snapshotFindings) byId.set(canonicalFindingId(finding), finding);
  for (const finding of persistedFindings) byId.set(canonicalFindingId(finding), finding);
  const evidence = history?.evidence ?? (Array.isArray(records?.evidence) ? records.evidence : []);
  let findings = [...byId.values()].filter(isSecurityFinding);
  if (filters.lane) {
    findings = findings.filter((finding) => findingTaskLane(finding, evidence) === filters.lane);
  }
  return {
    attemptId: records?.attempt?.attemptId ?? null,
    attempt: records?.attempt ?? null,
    findings,
    evidence,
    sourceRecords:
      history?.sourceRecords ??
      (Array.isArray(records?.sourceRecords) ? records.sourceRecords : []),
    findingEngagementLinks: Array.isArray(records?.findingEngagementLinks)
      ? records.findingEngagementLinks
      : [],
  };
}

/** Findings union the latest snapshot with the persisted workspace store; persisted rows win on id. */
export const findingsClient = {
  latestAttempt,

  evidence: (workspaceId: string, evidenceId: string, credentials: WorkspaceCredentials = {}) =>
    api.getDiscoveryEvidenceRecord(workspaceId, evidenceId, credentials.session, credentials.token),

  async fromLatestAttempt(
    workspaceId: string,
    findingId: string,
    credentials: WorkspaceCredentials = {},
  ): Promise<Finding | null> {
    try {
      const row = await discoveryClient.finding(workspaceId, findingId.trim(), credentials);
      if (isPersistedFinding(row) && canonicalFindingId(row) === findingId.trim()) return row;
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 404) throw error;
    }
    const { findings } = await latestAttempt(workspaceId, credentials);
    return findings.find((finding) => canonicalFindingId(finding) === findingId.trim()) ?? null;
  },
};
